/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-12-26 14:52:22                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-27 13:23:11                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Components
  * Services
  * InputCompinfoForm: compinfo input type form component
    * useEffect: keep dataState update
    * useEffect: Get compinfo list
    * handleChange: handle input change
    * displayCompInfo: display proper compinfo input
*/

/* Imports */
import { useEffect, useState } from "react";
/***/

/* Components */
import Loading from "../../loading/Loading";
import CompInfoInputText from "./input/CompInfoInputText";
import CompInfoInputTextarea from "./input/CompInfoInputTextarea";
import CompInfoInputPassword from "./input/CompInfoInputPassword";
import CompInfoInputDate from "./input/CompInfoInputDate";
import CompInfoInputNumber from "./input/CompInfoInputNumber";
import Switch from "../../switch/Switch";
/***/

/* Services */
import { api } from "../../../services/api/api";
import { validator } from "../../../services/form-validator/form-validator";
import { allowedCompInfoUsages, isCompInfoUsable } from "./compinfo-usage";
import { getCompInfoLabel } from "./compinfo-errors";
/***/

/**
* InputCompinfoForm: compinfo input type form component
* @param data - for editiong, current value
* @param onChange - on data change event
* @param onSubmit - on submit event
* @param submitted - submitted state
* @param ressource - compinfo ressource type
* @param status - statut de l'intervention, absent à la création
*/
export default function InputCompinfoForm({
  data,
  onChange,
  onSubmit,
  submitted,
  ressource,
  status
}: {
  data?: EasyTech.InputCompinfo[]
  onChange: (e: EasyTech.InputCompinfo[]) => void
  onSubmit: () => void
  submitted?: boolean,
  ressource: string,
  status?: string
}) {
  const [ciList, setCiList] = useState<EasyTech.CompInfo[]>();
  const [dataState, setDataState] = useState<EasyTech.InputCompinfo[]>(data || []);

  /**
  * useEffect: keep dataState update
  * @param data - reload on data change
  */
  useEffect(() => {
    setDataState(data || []);
  }, [data]);
  /***/

  /**
  * useEffect: Get compinfo list
  * @param ressource - reload on ressource change
  */
  useEffect(() => {
    api.get("users/compinfos/inputs")
      .then((ret) => {
        // Voir compinfo-usage : le statut dit quels moments de saisie sont ouverts
        const usages = allowedCompInfoUsages(status);
        const filteredList = (ret as EasyTech.CompInfo[])
          .filter((el) => el.service === ressource && isCompInfoUsable(el, usages));
        
        // Tri par order (propriété order du compInfo)
        const sortedList = filteredList.sort((a, b) => {
          if (a.order === undefined) return 1;
          if (b.order === undefined) return -1;
          return a.order - b.order;
        });
        
        setCiList(sortedList);
      }).catch((err) => {
        console.error(err);
        setCiList([]);
      });
  }, [ressource, status]);
  /***/

  /**
  * handleChange: handle input change
  * @param ci - compinfo
  * @param value - new value 
  */
  const handleChange = (ci: EasyTech.CompInfo, value: string) => {
    const existingCompinfo = dataState.find((el) => el.compInfo && el.compInfo.id === ci.id);
    
    const tmp: EasyTech.InputCompinfo = {
      compInfo: ci,
      value: value
    };
    
    if (existingCompinfo && existingCompinfo.id) {
      tmp.id = existingCompinfo.id;
    }
    
    const ret = [...dataState];
    const index = dataState.findIndex((el) => el.compInfo && el.compInfo.id === ci.id);
    
    if (index !== -1) ret[index] = tmp;
    else ret.push(tmp);

    if (onChange) onChange(ret);
  };
  /***/

  /**
  * displayCompInfo: display proper compinfo input
  * @param ci - compinfo descriptor
  * @return - Compinfo input component
  */
  const displayCompInfo = (ci: EasyTech.CompInfo) => {
    const value = dataState.find((el) => el.compInfo.id === ci.id)?.value || "";
    const invalid = !validator.isValidCompInfo(ci, value);
    // Un e-mail mal saisi est signalé tout de suite, sans attendre l'envoi du formulaire.
    const showError = invalid && (submitted || (ci.type === "EMAIL" && String(value).trim() !== ""));

    if (ci.type === "TEXT" || ci.type === "PHONE" || ci.type === "EMAIL" || ci.type === "TIME")
      return <CompInfoInputText className={`${showError && "error"}`}
                                type={ci.type === "EMAIL" ? "email" : ci.type === "PHONE" ? "tel" : ci.type === "TIME" ? "time" : "text"}
                                value={value}
                                onChange={(e) => handleChange(ci, e)}/>;
    if (ci.type === "NUMBER")
      return <CompInfoInputNumber className={`${(submitted && !validator.isValidCompInfo(ci, value)) && "error"}`}
                                  value={value} 
                                  onChange={(e) => handleChange(ci, e)}/>;
    if (ci.type === "TEXTAREA")
      return <CompInfoInputTextarea className={`${(submitted && !validator.isValidCompInfo(ci, value)) && "error"}`}
                                    value={value}
                                    onChange={(e) => handleChange(ci, e)}/>;
    if (ci.type === "PASSWORD")
      return <CompInfoInputPassword className={`${(submitted && !validator.isValidCompInfo(ci, value)) && "error"}`}
                                    value={value}
                                    onChange={(e) => handleChange(ci, e)}/>;
    if (ci.type === "DATE" || ci.type === "DATETIME")
      return <CompInfoInputDate className={`${(submitted && !validator.isValidCompInfo(ci, value)) && "error"}`}
                                showTimeSelect={ci.type === "DATETIME"}
                                value={value}
                                onChange={(e) => handleChange(ci, e)}/>;
    if (ci.type === "BOOLEAN")
      return <Switch className={`${(submitted && !validator.isValidCompInfo(ci, value)) && "error"}`}
                     checked={value === "true" || (value as any) === true}
                     onChange={(checked: boolean) => handleChange(ci, checked.toString())}/>;
  };
  /***/

  return (
    <div className="contents">
      {ciList ?
        <>
          {ciList.map((ci) => (
            ci.type === "BOOLEAN" ? (
              <div key={ci.id} className="field flex flex-row items-center justify-between" style={{ order: ci.order ?? 9999 }}>
                <label>{getCompInfoLabel(ci)} {ci.constraints === "REQUIRED" && "*"}</label>
                <div className="ml-auto">
                  {displayCompInfo(ci)}
                </div>
              </div>
            ) : (
              <div key={ci.id} className="field" style={{ order: ci.order ?? 9999 }}>
                <label>{getCompInfoLabel(ci)} {ci.constraints === "REQUIRED" && "*"}</label>
                {displayCompInfo(ci)}
              </div>
            )
          ))}
        </>
      : <Loading size="h-5 w-5"/>}
    </div>
  );
}
/***/
